import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../PageEditor/PageEditor.css";
import "./PostEditor.css";
import useCmsStore from "../../../services/useCmsStore";
import { toast } from "react-toastify";
import {
  FiArrowLeft,
  FiSave,
  FiEye,
  FiSend,
  FiClock,
  FiSettings,
  FiImage,
  FiSearch,
  FiLink,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiFileText,
  FiFolder,
  FiTag,
  FiUser,
  FiStar,
  FiGlobe,
  FiLayout,
} from "react-icons/fi";
import TipTapEditor from "../Editor/TipTapEditor";
import { MediaPicker } from "../Media";
import { SeoPanel } from "../SEO";
import { AIGenerateButton } from "../AI";
import { PreviewPanel } from "../Preview";

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const {
    selectedPost,
    postsLoading,
    categories,
    tags,
    authors,
    fetchPost,
    fetchCategories,
    fetchTags,
    fetchAuthors,
    createPost,
    updatePost,
    publishPost,
    calculateSeoScore,
    seoScore,
    clearSelectedPost,
  } = useCmsStore();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    content: null,
    excerpt: "",
    status: "draft",
    category_id: null,
    author_id: null,
    tag_ids: [],
    featured_image_id: null,
    featured_image: null,
    is_featured: false,
    allow_comments: true,
    // SEO
    seo_title: "",
    seo_description: "",
    seo_keywords: [],
    focus_keyword: "",
    robots_index: true,
    robots_follow: true,
    canonical_url: "",
    // Open Graph
    og_title: "",
    og_description: "",
    og_image_id: null,
    // Sitemap
    sitemap_priority: 0.6,
    sitemap_frequency: "weekly",
    exclude_from_sitemap: false,
  });

  const [expandedSections, setExpandedSections] = useState({
    categorization: true,
    general: false,
    seo: true,
    og: false,
    sitemap: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");
  const [tagSearch, setTagSearch] = useState("");

  // Load data
  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchAuthors();
    if (!isNew && id) {
      fetchPost(id);
    }
    return () => clearSelectedPost();
  }, [id, isNew, fetchPost, fetchCategories, fetchTags, fetchAuthors, clearSelectedPost]);

  // Populate form when post is loaded
  useEffect(() => {
    if (selectedPost && !isNew) {
      setFormData({
        title: selectedPost.title || "",
        slug: selectedPost.slug || "",
        content: selectedPost.content || null,
        excerpt: selectedPost.excerpt || "",
        status: selectedPost.status || "draft",
        category_id: selectedPost.category_id || null,
        author_id: selectedPost.author_id || null,
        tag_ids: selectedPost.tags?.map(t => t.id) || [],
        featured_image_id: selectedPost.featured_image_id || null,
        is_featured: selectedPost.is_featured || false,
        allow_comments: selectedPost.allow_comments ?? true,
        seo_title: selectedPost.seo?.title || "",
        seo_description: selectedPost.seo?.description || "",
        seo_keywords: selectedPost.seo?.keywords || [],
        focus_keyword: selectedPost.seo?.focus_keyword || "",
        robots_index: selectedPost.seo?.robots_index ?? true,
        robots_follow: selectedPost.seo?.robots_follow ?? true,
        canonical_url: selectedPost.seo?.canonical_url || "",
        og_title: selectedPost.og?.title || "",
        og_description: selectedPost.og?.description || "",
        og_image_id: selectedPost.og?.image_id || null,
        sitemap_priority: selectedPost.sitemap?.priority || 0.6,
        sitemap_frequency: selectedPost.sitemap?.frequency || "weekly",
        exclude_from_sitemap: selectedPost.sitemap?.exclude || false,
      });
    }
  }, [selectedPost, isNew]);

  // Auto-generate slug from title
  const generateSlug = useCallback((title) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "title" && (isNew || !prev.slug)) {
        newData.slug = generateSlug(value);
      }
      // Update featured_image_id when featured_image changes
      if (field === "featured_image") {
        newData.featured_image_id = value?.id || null;
      }
      return newData;
    });
    setHasChanges(true);
  };

  const handleSave = async (publish = false) => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSave = {
        ...formData,
        status: publish ? "published" : formData.status,
      };

      if (isNew) {
        const newPost = await createPost(dataToSave);
        toast.success("Post created successfully");
        navigate(`/cms/posts/${newPost.id}`);
      } else {
        await updatePost(id, dataToSave);
        toast.success(publish ? "Post published successfully" : "Post saved successfully");
      }
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to save post");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!isNew && formData.status !== "published") {
      try {
        await publishPost(id);
        toast.success("Post published successfully");
        setHasChanges(false);
      } catch (error) {
        toast.error("Failed to publish post");
      }
    } else {
      handleSave(true);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleTag = (tagId) => {
    const newTags = formData.tag_ids.includes(tagId)
      ? formData.tag_ids.filter(id => id !== tagId)
      : [...formData.tag_ids, tagId];
    handleChange("tag_ids", newTags);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.seo_keywords.includes(keywordInput.trim())) {
      handleChange("seo_keywords", [...formData.seo_keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (keyword) => {
    handleChange("seo_keywords", formData.seo_keywords.filter(k => k !== keyword));
  };

  const getSeoScoreColor = () => {
    if (!seoScore) return "#6B7280";
    if (seoScore.score >= 70) return "#10B981";
    if (seoScore.score >= 40) return "#F59E0B";
    return "#EF4444";
  };

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  // Calculate SEO score
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (formData.title || formData.content) {
        calculateSeoScore({
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          seo_title: formData.seo_title,
          seo_description: formData.seo_description,
          focus_keyword: formData.focus_keyword,
        });
      }
    }, 1000);
    return () => clearTimeout(debounce);
  }, [formData.title, formData.content, formData.excerpt, formData.seo_title, formData.seo_description, formData.focus_keyword, calculateSeoScore]);

  if (postsLoading && !isNew) {
    return (
      <div className="page-editor-loading">
        <div className="loading-spinner"></div>
        <p>Loading post...</p>
      </div>
    );
  }

  return (
    <div className="page-editor post-editor">
      {/* Header */}
      <div className="page-editor-header">
        <div className="page-editor-header-left">
          <button className="btn-back" onClick={() => navigate("/cms")}>
            <FiArrowLeft size={20} />
          </button>
          <div className="page-editor-title-section">
            <h1>{isNew ? "New Post" : "Edit Post"}</h1>
            {hasChanges && <span className="unsaved-badge">Unsaved changes</span>}
          </div>
        </div>
        <div className="page-editor-header-right">
          <button className="btn-secondary" onClick={() => setShowPreview(!showPreview)}>
            <FiEye size={16} />
            <span>Preview</span>
          </button>
          <button className="btn-secondary" onClick={() => handleSave(false)} disabled={isSaving}>
            <FiSave size={16} />
            <span>{isSaving ? "Saving..." : "Save Draft"}</span>
          </button>
          <button className="btn-primary" onClick={handlePublish} disabled={isSaving}>
            <FiSend size={16} />
            <span>Publish</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-editor-main">
        {/* Left Panel - Settings */}
        <div className="page-editor-sidebar">
          {/* SEO Analysis Panel */}
          <SeoPanel
            title={formData.title}
            content={formData.content}
            excerpt={formData.excerpt}
            focusKeyword={formData.focus_keyword}
            seoTitle={formData.seo_title}
            seoDescription={formData.seo_description}
            slug={formData.slug}
          />

          {/* Categorization */}
          <div className="settings-section">
            <button className="section-header" onClick={() => toggleSection("categorization")}>
              <div className="section-title">
                <FiFolder size={16} />
                <span>Category & Tags</span>
              </div>
              {expandedSections.categorization ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.categorization && (
              <div className="section-content">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category_id || ""}
                    onChange={(e) => handleChange("category_id", e.target.value || null)}
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Author</label>
                  <select
                    value={formData.author_id || ""}
                    onChange={(e) => handleChange("author_id", e.target.value || null)}
                  >
                    <option value="">Select author...</option>
                    {authors.map((author) => (
                      <option key={author.id} value={author.id}>
                        {author.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search tags..."
                  />
                  <div className="tags-selector">
                    {filteredTags.slice(0, 20).map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className={`tag-option ${formData.tag_ids.includes(tag.id) ? "selected" : ""}`}
                        onClick={() => toggleTag(tag.id)}
                      >
                        {formData.tag_ids.includes(tag.id) && <FiCheck size={12} />}
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => handleChange("is_featured", e.target.checked)}
                    />
                    <FiStar size={14} />
                    <span>Featured post</span>
                  </label>
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.allow_comments}
                      onChange={(e) => handleChange("allow_comments", e.target.checked)}
                    />
                    <span>Allow comments</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* SEO Settings */}
          <div className="settings-section">
            <button className="section-header" onClick={() => toggleSection("seo")}>
              <div className="section-title">
                <FiSearch size={16} />
                <span>SEO Settings</span>
              </div>
              {expandedSections.seo ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.seo && (
              <div className="section-content">
                <div className="form-group">
                  <label>Focus Keyword</label>
                  <input
                    type="text"
                    value={formData.focus_keyword}
                    onChange={(e) => handleChange("focus_keyword", e.target.value)}
                    placeholder="e.g., digital marketing"
                  />
                </div>
                <div className="form-group">
                  <label>
                    SEO Title
                    <span className="char-count" style={{ color: formData.seo_title.length > 60 ? "#EF4444" : "#6B7280" }}>
                      {formData.seo_title.length}/60
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.seo_title}
                    onChange={(e) => handleChange("seo_title", e.target.value)}
                    placeholder={formData.title || "Enter SEO title"}
                    maxLength={70}
                  />
                </div>
                <div className="form-group">
                  <label>
                    Meta Description
                    <span className="char-count" style={{ color: formData.seo_description.length > 160 ? "#EF4444" : "#6B7280" }}>
                      {formData.seo_description.length}/160
                    </span>
                  </label>
                  <textarea
                    value={formData.seo_description}
                    onChange={(e) => handleChange("seo_description", e.target.value)}
                    placeholder="Enter meta description..."
                    rows={3}
                    maxLength={160}
                  />
                </div>
                <div className="form-group">
                  <label>SEO Keywords</label>
                  <div className="keyword-input-wrapper">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
                      placeholder="Add keyword"
                    />
                    <button type="button" onClick={addKeyword}>Add</button>
                  </div>
                  {formData.seo_keywords.length > 0 && (
                    <div className="keywords-list">
                      {formData.seo_keywords.map((keyword, idx) => (
                        <span key={idx} className="keyword-tag">
                          {keyword}
                          <button onClick={() => removeKeyword(keyword)}>
                            <FiX size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* General Settings */}
          <div className="settings-section">
            <button className="section-header" onClick={() => toggleSection("general")}>
              <div className="section-title">
                <FiSettings size={16} />
                <span>General Settings</span>
              </div>
              {expandedSections.general ? <FiChevronUp /> : <FiChevronDown />}
            </button>
            {expandedSections.general && (
              <div className="section-content">
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Canonical URL</label>
                  <input
                    type="url"
                    value={formData.canonical_url}
                    onChange={(e) => handleChange("canonical_url", e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="form-group-inline">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.robots_index}
                      onChange={(e) => handleChange("robots_index", e.target.checked)}
                    />
                    <span>Allow indexing</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Content Editor */}
        <div className="page-editor-content">
          <div className="content-header">
            <div className="form-group title-with-ai">
              <label>Title</label>
              <div className="title-input-row">
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  placeholder="Enter post title..."
                  className="title-input"
                />
                <AIGenerateButton
                  title={formData.title}
                  type="article"
                  language="pt-BR"
                  onGenerated={(result) => {
                    if (result.content) handleChange("content", result.content);
                    if (result.excerpt) handleChange("excerpt", result.excerpt);
                    if (result.seo_description) handleChange("seo_description", result.seo_description);
                    if (result.suggested_tags && result.suggested_tags.length > 0) {
                      // Match suggested tags with existing tags
                      const matchedTagIds = tags
                        .filter(t => result.suggested_tags.some(st =>
                          t.name.toLowerCase().includes(st.toLowerCase()) ||
                          st.toLowerCase().includes(t.name.toLowerCase())
                        ))
                        .map(t => t.id);
                      if (matchedTagIds.length > 0) {
                        handleChange("tag_ids", matchedTagIds);
                      }
                    }
                    toast.success("Content generated successfully!");
                  }}
                />
              </div>
            </div>
            <div className="form-group slug-group">
              <label>
                <FiLink size={14} />
                <span>Slug</span>
              </label>
              <div className="slug-input-wrapper">
                <span className="slug-prefix">/blog/</span>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="post-slug"
                />
              </div>
            </div>
          </div>

          <div className="content-body">
            <div className="form-group">
              <label>Excerpt</label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => handleChange("excerpt", e.target.value)}
                placeholder="Brief summary of the post..."
                rows={3}
              />
            </div>

            <div className="form-group content-editor-group">
              <label>Content</label>
              <TipTapEditor
                content={formData.content}
                onChange={(content) => handleChange("content", content)}
                placeholder="Start writing your post content or type / for commands..."
                onImageUpload={async (file) => {
                  // TODO: Integrate with media library upload
                  return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                  });
                }}
              />
            </div>
          </div>

          <div className="featured-image-section">
            <MediaPicker
              value={formData.featured_image}
              onChange={(media) => handleChange("featured_image", media)}
              type="image"
              label="Featured Image"
              placeholder="Click to select a featured image"
            />
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      <PreviewPanel
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        content={formData.content}
        title={formData.title}
        excerpt={formData.excerpt}
        featuredImage={formData.featured_image}
      />
    </div>
  );
};

export default PostEditor;
